import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { DocumentBulletList20Regular } from '@fluentui/react-icons';
import { ControlledField } from '@shared/components/ControlledField';
import { FormDialog } from '@shared/components/dialogs/FormDialog';
import { TERMINOLOGY } from '@shared/i18n/terminology';
import { DEFAULT_CHANGE_TEMPLATE_FORM, changeTemplateFormSchema } from './changeTemplateForm';
import type { TChangeTemplateFormValues } from '../types';

type TProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onCreate: (values: TChangeTemplateFormValues) => Promise<unknown>;
};

const ChangeTemplateDialog = ({ isOpen, setIsOpen, onCreate }: TProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TChangeTemplateFormValues>({
    resolver: yupResolver(changeTemplateFormSchema),
    mode: 'onBlur',
    defaultValues: DEFAULT_CHANGE_TEMPLATE_FORM,
  });

  useEffect(() => {
    if (isOpen) reset(DEFAULT_CHANGE_TEMPLATE_FORM);
  }, [isOpen]);

  const onSubmit = async (values: TChangeTemplateFormValues) => {
    try {
      await onCreate(values);
      setIsOpen(false);
    } catch {
      /* The API hook reports the failure. Preserve the form for retry. */
    }
  };

  return (
    <FormDialog
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={
        <div className="flex items-center space-x-2">
          <DocumentBulletList20Regular className="shrink-0" />
          <span>Create {TERMINOLOGY.changeTemplate.lower}</span>
        </div>
      }
      onSubmit={handleSubmit(onSubmit)}
      submitLabel="Create"
      submitIcon={null}
      isSubmitting={isSubmitting}
      contentClassName="max-h-[70vh] overflow-y-auto"
    >
      <div className="flex flex-col gap-4">
        <ControlledField
          inputType="input"
          name="title"
          label="Title"
          placeholder={`Enter ${TERMINOLOGY.changeTemplate.lower} title`}
          required
          control={control}
          errors={errors}
        />
        <ControlledField
          inputType="textarea"
          name="description"
          label="Description"
          placeholder="Add a description"
          rows={4}
          resize="vertical"
          control={control}
          errors={errors}
        />
      </div>
    </FormDialog>
  );
};

export default ChangeTemplateDialog;
